/* =========================================================
   PH PRODUCT PRICE v4.0

   PRODUCTOS NORMALES:
   - Conservan el motor PH de promociones por volumen.

   PAQUETES:
   - Shopify conserva el precio BASE/NORMAL.
   - El descuento se obtiene de la variante:
       1 = 0%
       2 = 10%
       3 = 15%
       5 = 20%
      10 = 20%
   - El precio final mostrado se calcula BASE x descuento.
   ========================================================= */

class PHProductPrice extends HTMLElement {

  constructor() {
    super();

    this.variantPrices = {};
    this.currentVariantId = null;

    this.currentPromotion = {
      discount: 0,
      priceDiscount: 0,
      tier: 0,
      mode: "volume"
    };
  }


  /* =====================================================
     INIT
     ===================================================== */

  connectedCallback() {

    this.priceNode =
      this.querySelector("[data-ph-price]");

    this.compareWrap =
      this.querySelector("[data-ph-compare-wrap]");

    this.compareNode =
      this.querySelector("[data-ph-compare]");

    this.discountBadge =
      this.querySelector("[data-ph-discount-badge]");

    this.quantityNode =
      this.querySelector("[data-ph-price-quantity]");

    this.unitNode =
      this.querySelector("[data-ph-price-unit]");

    this.packageSaving =
      this.querySelector("[data-ph-package-saving]");

    this.parseVariantPrices();

    this.currentVariantId =
      String(
        this.dataset.currentVariantId || ""
      );

    this.bindVariantChanges();
    this.bindPromotionChanges();

    if (window.PHPromotions?.state) {
      this.currentPromotion = {
        ...this.currentPromotion,
        ...window.PHPromotions.state
      };
    }

    this.render();
  }


  /* =====================================================
     VARIANTES
     ===================================================== */

  parseVariantPrices() {

    const node =
      this.querySelector("[data-ph-price-variants]");

    if (!node) {
      return;
    }

    try {

      this.variantPrices =
        JSON.parse(node.textContent);

    } catch (error) {

      console.warn(
        "PH Price: error leyendo precios.",
        error
      );

      this.variantPrices = {};
    }
  }


  /* =====================================================
     FORMATO MONEDA
     ===================================================== */

  formatMoney(cents) {

    const amount =
      Number(cents) / 100;

    if (!Number.isFinite(amount)) {
      return "";
    }

    return new Intl.NumberFormat(
      "es-MX",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(amount);
  }


  /* =====================================================
     PRODUCT ROOT
     ===================================================== */

  getProductRoot() {

    return (
      this.closest("product-info")
      ||
      document.querySelector(
        `product-info[data-product-id="${this.dataset.productId}"]`
      )
    );
  }


  /* =====================================================
     VARIANTE ACTUAL
     ===================================================== */

  getCurrentVariantId() {

    const productRoot =
      this.getProductRoot();

    if (!productRoot) {
      return (
        this.currentVariantId ||
        this.dataset.currentVariantId
      );
    }

    const variantInput =
      productRoot.querySelector(
        'form[action*="/cart/add"] input[name="id"]'
      )
      ||
      productRoot.querySelector(
        'input[name="id"]'
      );

    if (variantInput?.value) {
      return String(variantInput.value);
    }

    return (
      this.currentVariantId ||
      this.dataset.currentVariantId
    );
  }


  /* =====================================================
     CAMBIO DE VARIANTE
     ===================================================== */

  bindVariantChanges() {

    const productRoot =
      this.getProductRoot();

    if (!productRoot) {
      return;
    }

    const update = () => {

      window.setTimeout(
        () => {

          const variantId =
            this.getCurrentVariantId();

          if (!variantId) {
            return;
          }

          this.currentVariantId =
            String(variantId);

          this.dataset.currentVariantId =
            String(variantId);

          this.render();

        },
        45
      );
    };

    productRoot.addEventListener(
      "change",
      update
    );

    productRoot.addEventListener(
      "variant:change",
      update
    );

    document.addEventListener(
      "variant:change",
      update
    );
  }


  /* =====================================================
     PROMOCIÓN PH
     ===================================================== */

  bindPromotionChanges() {

    document.addEventListener(
      "ph:promotion:update",
      event => {

        const detail =
          event.detail || {};

        this.currentPromotion = {
          ...this.currentPromotion,
          ...detail
        };

        this.render();
      }
    );

    document.addEventListener("ph:remate:update", () => this.render());
  }


  /* =====================================================
     REMATE DE INVENTARIO
     ===================================================== */

  getRemateDiscount() {
    const active = document.body?.classList.contains("ph-theme-remate");
    const eligible = this.dataset.phRemateEligible === "true";
    if (!active || !eligible) return 0;
    const discount = Number(this.dataset.phRemateDiscount || 20);
    return discount === 40 ? 40 : 20;
  }


  /* =====================================================
     DESCUENTO NORMAL PH
     ===================================================== */

  getPromotionDiscount() {

    const promotion =
      this.currentPromotion || {};

    if (promotion.mode !== "volume") {
      return 0;
    }

    const discount =
      Number(
        promotion.priceDiscount
      ) || 0;

    if (
      discount === 10 ||
      discount === 15 ||
      discount === 20
    ) {
      return discount;
    }

    return 0;
  }


  getSelectedQuantity() {

    const productRoot =
      this.getProductRoot();

    const input =
      productRoot?.querySelector(
        'quantity-input input[name="quantity"], input[name="quantity"]'
      );

    const quantity = Number(
      this.currentPromotion?.selectedQuantity ?? input?.value ?? 1
    );

    return Number.isFinite(quantity) && quantity > 0
      ? Math.floor(quantity)
      : 1;
  }


  renderUnitContext(variant) {

    const isPackage =
      variant?.is_package === true ||
      variant?.is_package === "true";

    if (this.quantityNode) {
      const quantity = this.getSelectedQuantity();
      this.quantityNode.textContent = `${quantity} ${quantity === 1 ? "pza" : "pzas"} ·`;
      this.quantityNode.classList.toggle("is-hidden", isPackage);
    }

    this.unitNode?.classList.toggle("is-hidden", isPackage);
  }


  /* =====================================================
     CALCULAR DESCUENTO
     ===================================================== */

  calculateDiscountedPrice(
    priceCents,
    discount
  ) {

    const base =
      Number(priceCents);

    if (!Number.isFinite(base)) {
      return null;
    }

    if (!discount) {
      return base;
    }

    return Math.round(
      base * (1 - discount / 100)
    );
  }


  /* =====================================================
     LIMPIAR ESTADO
     ===================================================== */

  clearDiscountVisuals() {

    if (this.discountBadge) {
      this.discountBadge.textContent = "";
      this.discountBadge.classList.add(
        "is-hidden"
      );
    }

    if (this.packageSaving) {
      this.packageSaving.textContent = "";
      this.packageSaving.classList.add(
        "is-hidden"
      );
    }

    this.classList.remove(
      "has-ph-discount"
    );
  }


  /* =====================================================
     PRECIO NORMAL
     ===================================================== */

  renderRegularPrice(variant) {

    if (
      !variant ||
      !this.priceNode
    ) {
      return;
    }

    this.priceNode.textContent =
      variant.price;

    if (variant.compare) {

      if (this.compareNode) {
        this.compareNode.textContent =
          variant.compare;
      }

      this.compareWrap
        ?.classList
        .remove("is-hidden");

    } else {

      if (this.compareNode) {
        this.compareNode.textContent = "";
      }

      this.compareWrap
        ?.classList
        .add("is-hidden");
    }

    this.clearDiscountVisuals();
  }


  /* =====================================================
     PRODUCTOS NORMALES CON PROMOCIÓN PH
     ===================================================== */

  renderPromotionalPrice(
    variant,
    discount
  ) {

    if (
      !variant ||
      !this.priceNode
    ) {
      return;
    }

    const basePrice =
      Number(
        variant.price_cents
      );

    if (!Number.isFinite(basePrice)) {
      this.renderRegularPrice(variant);
      return;
    }

    const discountedPrice =
      this.calculateDiscountedPrice(
        basePrice,
        discount
      );

    if (discountedPrice === null) {
      this.renderRegularPrice(variant);
      return;
    }

    this.priceNode.textContent =
      this.formatMoney(
        discountedPrice
      );

    if (this.compareNode) {
      this.compareNode.textContent =
        this.formatMoney(
          basePrice
        );
    }

    this.compareWrap
      ?.classList
      .remove("is-hidden");

    if (this.discountBadge) {

      this.discountBadge.textContent =
        `${discount}% OFF`;

      this.discountBadge
        .classList
        .remove("is-hidden");
    }

    if (this.packageSaving) {
      this.packageSaving.textContent = "";
      this.packageSaving
        .classList
        .add("is-hidden");
    }

    this.classList.add(
      "has-ph-discount"
    );
  }


  /* =====================================================
     PAQUETES

     variant.price_cents = PRECIO BASE NORMAL
     discount_percent    = descuento por Piezas
     ===================================================== */

  renderPackagePrice(variant) {

    if (
      !variant ||
      !this.priceNode
    ) {
      return;
    }

    const basePrice =
      Number(
        variant.price_cents
      );

    const discount =
      Number(
        variant.discount_percent
      ) || 0;

    if (!Number.isFinite(basePrice)) {
      this.renderRegularPrice(variant);
      return;
    }

    /*
      1 pieza / variante sin descuento
    */

    if (discount <= 0) {

      this.priceNode.textContent =
        this.formatMoney(basePrice);

      if (
        variant.compare &&
        this.compareNode
      ) {

        this.compareNode.textContent =
          variant.compare;

        this.compareWrap
          ?.classList
          .remove("is-hidden");

      } else {

        if (this.compareNode) {
          this.compareNode.textContent = "";
        }

        this.compareWrap
          ?.classList
          .add("is-hidden");
      }

      this.clearDiscountVisuals();

      return;
    }


    /*
      Aplicamos el descuento AL PRECIO BASE.
    */

    const discountedPrice =
      this.calculateDiscountedPrice(
        basePrice,
        discount
      );

    if (discountedPrice === null) {
      this.renderRegularPrice(variant);
      return;
    }


    /*
      Precio final
    */

    this.priceNode.textContent =
      this.formatMoney(
        discountedPrice
      );


    /*
      Precio original tachado
    */

    if (this.compareNode) {
      this.compareNode.textContent =
        this.formatMoney(
          basePrice
        );
    }

    this.compareWrap
      ?.classList
      .remove("is-hidden");


    /*
      Badge
    */

    if (this.discountBadge) {

      this.discountBadge.textContent =
        `${discount}% OFF`;

      this.discountBadge
        .classList
        .remove("is-hidden");
    }


    /*
      Ahorro
    */

    if (this.packageSaving) {

      this.packageSaving.textContent =
        `Ahorra ${discount}%`;

      this.packageSaving
        .classList
        .remove("is-hidden");
    }


    this.classList.add(
      "has-ph-discount"
    );
  }


  /* =====================================================
     RENDER
     ===================================================== */

  render() {

    const variantId =
      String(
        this.getCurrentVariantId()
        ||
        this.currentVariantId
        ||
        ""
      );

    if (!variantId) {
      return;
    }

    const variant =
      this.variantPrices[
        variantId
      ];

    if (!variant) {
      return;
    }

    this.currentVariantId =
      variantId;

    this.dataset.currentVariantId =
      variantId;

    this.renderUnitContext(variant);


    /*
      PAQUETES:
      descuento según Piezas.
    */

    if (
      variant.is_package === true ||
      variant.is_package === "true"
    ) {

      this.renderPackagePrice(
        variant
      );

      return;
    }


    /*
      REMATE DE INVENTARIO:
      prioridad sobre descuentos por volumen mientras esté activo.
    */
    const remateDiscount = this.getRemateDiscount();
    if (remateDiscount > 0) {
      this.renderPromotionalPrice(variant, remateDiscount);
      if (this.discountBadge) {
        this.discountBadge.textContent = "";
        this.discountBadge.classList.add("is-hidden");
      }
      return;
    }

    /*
      PRODUCTOS NORMALES:
      promoción PH existente.
    */

    const discount =
      this.getPromotionDiscount();

    if (discount <= 0) {

      this.renderRegularPrice(
        variant
      );

      return;
    }

    this.renderPromotionalPrice(
      variant,
      discount
    );
  }
}


/* =========================================================
   REGISTRO
   ========================================================= */

if (
  !customElements.get(
    "ph-product-price"
  )
) {

  customElements.define(
    "ph-product-price",
    PHProductPrice
  );
}
